import {useCallback} from "react";
import {useFormContext} from "react-hook-form";
import type {LevelGeometryDocument} from "@/dungeons/model/DungeonGeometry.ts";
import {useDungeonEditorHistory} from "@/dungeons/editor/history/DungeonEditorHistory.tsx";
import type {DungeonFormInput, DungeonFormOutput} from "@/dungeons/admin/form/DungeonFormSchema.ts";
import {createDirectionalLight} from "@/dungeons/lighting/DirectionalLight.ts";

export type GeometryDocumentUpdate = (document: LevelGeometryDocument) => LevelGeometryDocument;

export default function useLevelGeometryDocument(
    {
        levelId,
        sourceRevision,
        document,
    }: {
        levelId: string;
        sourceRevision: number;
        document: LevelGeometryDocument;
    }) {
    const form = useFormContext<DungeonFormInput, unknown, DungeonFormOutput>();
    const sourceKey = `${levelId}:${sourceRevision}`;
    const {record} = useDungeonEditorHistory();

    const updateDocument = useCallback((update: GeometryDocumentUpdate) => {
        const levels = form.getValues("levels") ?? [];
        const levelIndex = levels.findIndex(level => level.id === levelId);
        if (levelIndex < 0) return;
        const currentDocument = {...levels[levelIndex].geometry,
            directionalLight: levels[levelIndex].geometry.directionalLight ?? createDirectionalLight(),
            lights: levels[levelIndex].geometry.lights ?? []};
        const nextDocument = update(currentDocument);
        if (nextDocument === currentDocument) return;
        record({levelId, before: currentDocument, after: nextDocument});
        form.setValue(`levels.${levelIndex}.geometry`, nextDocument, {shouldDirty: true, shouldValidate: true});
    }, [form, levelId, record]);

    return {document, sourceKey, updateDocument};
}
